import * as React from 'react';
import {
    Box,
    Card,
    Stack,
    Divider,
    Typography,
    FormControlLabel,
    Switch,
    FormGroup,
    Checkbox,
    Chip,
    Alert,
    alpha
} from '@mui/material';
import { Localized } from '@fluent/react';
import { CompanyConsent } from '../companyProfileApi';
import { COMPANY_CONSENT_TEXT, CompanyConsentTypeEnum } from '../enums/companyConsentEnum';

type Props = {
    marketActive: boolean;
    restrictVisibilityToBuyers: boolean;
    consents: CompanyConsent[];
    serverError?: string;
    onMarketActiveChange: (value: boolean) => void;
    onRestrictVisibilityChange: (value: boolean) => void;
    onConsentsChange: (consents: CompanyConsent[]) => void;
};

export const MarketplaceConsentsEditor: React.FC<Props> = ({
    marketActive,
    restrictVisibilityToBuyers,
    consents,
    serverError,
    onMarketActiveChange,
    onRestrictVisibilityChange,
    onConsentsChange,
}) => {
    // helper: wyciągnij numeric typeId z obiektu consent
    const toTypeId = (c: CompanyConsent): number | undefined => {
        if (typeof c.typeId === 'number') return c.typeId;
        const t = (c as any).type;
        if (typeof t === 'number') return t;
        if (typeof t === 'string') {
            const num = (CompanyConsentTypeEnum as any)[t];
            return typeof num === 'number' ? num : undefined;
        }
        return undefined;
    };

    const handleConsentChange = (typeNum: number, checked: boolean) => {
        const current = consents ?? [];
        const idx = current.findIndex(c => toTypeId(c) === typeNum);

        let newConsents: CompanyConsent[];

        if (checked) {
            // dodaj lub ustaw granted=true
            if (idx >= 0) {
                const next = [...current];
                next[idx] = { ...next[idx], typeId: typeNum, granted: true };
                newConsents = next;
            } else {
                newConsents = [...current, { typeId: typeNum, granted: true } as CompanyConsent];
            }
        } else {
            // odznacz: ustaw granted=false
            if (idx >= 0) {
                const next = [...current];
                next[idx] = { ...next[idx], typeId: typeNum, granted: false };
                newConsents = next;
            } else {
                newConsents = current;
            }
        }

        onConsentsChange(newConsents);
    };

    return (
        <Box>
            <Card variant="outlined" sx={{ p: 2 }}>
                <Box
                    sx={(theme) => ({
                        border: 2,
                        borderColor: 'success.main',
                        borderRadius: 2,
                        p: 2,
                        bgcolor: alpha(theme.palette.success.light, 0.06),
                    })}
                >
                    <Stack spacing={2} divider={<Divider flexItem />}>
                        {/* Activate Marketplace */}
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography variant="body1" fontWeight={700}>Activate Marketplace</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Makes your company profile visible in the supplier directory according to the consents below
                                </Typography>
                            </Box>
                            <FormControlLabel
                                control={<Switch checked={!!marketActive} onChange={(_, v) => onMarketActiveChange(v)} />}
                                label=""
                            />
                        </Stack>

                        {/* Restrict Visibility to Buyers */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={2}
                            sx={{ opacity: marketActive ? 1 : 0.6 }}
                        >
                            <Box>
                                <Typography variant="body1" fontWeight={700}>Restrict Visibility to Buyers</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    When enabled, your company profile will be hidden from other device suppliers and visible only to buyers.
                                </Typography>
                            </Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!restrictVisibilityToBuyers}
                                        onChange={(_, v) => onRestrictVisibilityChange(v)}
                                    />
                                }
                                label=""
                                disabled={!marketActive}
                            />
                        </Stack>
                    </Stack>
                </Box>

                <Divider sx={{ my: 2 }} />

                <FormGroup>
                    {(Object.values(CompanyConsentTypeEnum) as Array<string | number>)
                        .filter((v): v is number => typeof v === 'number')
                        .map((typeNum) => {
                            const def = COMPANY_CONSENT_TEXT[typeNum as CompanyConsentTypeEnum];
                            const label = def.label.en;
                            const description = def.description.en;
                            const required = def.required;
                            const disabled = !marketActive;

                            // zaznaczony, jeśli w consents mamy ten typeId z granted = true
                            const selected = (consents ?? []).some(
                                c => toTypeId(c) === typeNum && c.granted === true
                            );

                            return (
                                <Box key={typeNum} sx={{ mb: 1.5 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selected}
                                                disabled={disabled}
                                                onChange={(_, checked) => handleConsentChange(typeNum, checked)}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                    {label} {required && <Chip size="small"
                                                        color="warning" label="required" sx={{ ml: 1, p: 0 }} />}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {description}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Box>
                            );
                        })}
                </FormGroup>
            </Card>
            {serverError && (
                <Alert sx={{ marginTop: 1 }} severity="error">
                    <Localized id={serverError}>
                        <span style={{ whiteSpace: 'pre-line' }}>{serverError}</span>
                    </Localized>
                </Alert>
            )}
        </Box>
    );
};
